import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, ChevronRight, Download } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function HowToArrivePage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [crags, setCrags] = useState([]);
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cragsRes, trailsRes] = await Promise.all([
          axios.get(`${API}/crags`),
          axios.get(`${API}/trails`)
        ]);
        setCrags(cragsRes.data);
        setTrails(trailsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const directions = {
    it: {
      fromPalermo: 'Da Palermo: Prendere l\'autostrada A19 direzione Catania. Uscire a Scillato e seguire le indicazioni per Caltavuturo (circa 10 km).',
      fromCatania: 'Da Catania: Prendere l\'autostrada A19 direzione Palermo. Uscire a Scillato e seguire le indicazioni per Caltavuturo.',
      fromAirport: 'Dall\'aeroporto di Palermo: Prendere la A29 direzione Palermo, poi la A19 direzione Catania. Uscire a Scillato.',
      parking: 'Parcheggio gratuito disponibile nel centro del paese. Per le falesie, seguire le indicazioni specifiche per ogni settore.'
    },
    en: {
      fromPalermo: 'From Palermo: Take the A19 motorway towards Catania. Exit at Scillato and follow signs for Caltavuturo (about 10 km).',
      fromCatania: 'From Catania: Take the A19 motorway towards Palermo. Exit at Scillato and follow signs for Caltavuturo.',
      fromAirport: 'From Palermo airport: Take the A29 towards Palermo, then the A19 towards Catania. Exit at Scillato.',
      parking: 'Free parking available in the town center. For the crags, follow specific directions for each sector.'
    }
  };

  const dir = directions[language] || directions.en;

  return (
    <div data-testid="how-to-arrive-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Navigation className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('howToArrive')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Come raggiungere Caltavuturo e le nostre falesie.'
                : 'How to reach Caltavuturo and our crags.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Map */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="heading-md text-[#0A0A0A] mb-6">
                {language === 'it' ? 'Raggiungi Caltavuturo' : 'Reach Caltavuturo'}
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="font-heading uppercase mb-3">
                    {language === 'it' ? 'Da Palermo' : 'From Palermo'}
                  </h3>
                  <p className="text-gray-600 text-sm">{dir.fromPalermo}</p>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="font-heading uppercase mb-3">
                    {language === 'it' ? 'Da Catania' : 'From Catania'}
                  </h3>
                  <p className="text-gray-600 text-sm">{dir.fromCatania}</p>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="font-heading uppercase mb-3">
                    {language === 'it' ? 'Dall\'Aeroporto' : 'From Airport'}
                  </h3>
                  <p className="text-gray-600 text-sm">{dir.fromAirport}</p>
                </div>

                <div className="bg-white p-6 rounded-lg border">
                  <h3 className="font-heading uppercase mb-3">
                    {language === 'it' ? 'Parcheggio' : 'Parking'}
                  </h3>
                  <p className="text-gray-600 text-sm">{dir.parking}</p>
                </div>
              </div>

              <div className="mt-8">
                <a 
                  href="https://www.google.com/maps/dir//Caltavuturo,+PA,+Italy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="btn-primary">
                    <Navigation className="w-5 h-5 mr-2" />
                    {language === 'it' ? 'Ottieni indicazioni' : 'Get directions'}
                  </Button>
                </a>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden h-[500px]">
              <iframe
                title="Caltavuturo Map"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8'}&q=Caltavuturo,+PA,+Italy&zoom=12`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Crags Directions */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="heading-md text-[#0A0A0A] mb-12 text-center">
            {language === 'it' ? 'Come raggiungere le falesie' : 'How to reach the crags'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {crags.map((crag, i) => (
              <motion.div
                key={crag.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white border rounded-lg overflow-hidden hover:border-[#FF6D00]/50 transition-colors"
              >
                <div className="aspect-video relative">
                  <img 
                    src={crag.images?.[0] || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=400'}
                    alt={crag.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg uppercase mb-3">{crag.name}</h3>
                  <ul className="space-y-2 text-sm text-gray-600 mb-4">
                    <li className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#FF6D00]" />
                      <span>{crag.gps_lat?.toFixed(4)}, {crag.gps_lng?.toFixed(4)}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Navigation className="w-4 h-4 text-[#FF6D00]" />
                      <span>{getTranslatedContent(crag.approach_time)}</span>
                    </li>
                  </ul>
                  <a 
                    href={`https://www.google.com/maps?q=${crag.gps_lat},${crag.gps_lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-[#FF6D00] font-semibold text-sm hover:underline"
                  >
                    {language === 'it' ? 'Apri in Maps' : 'Open in Maps'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trails Section */}
      {trails.length > 0 && (
        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <h2 className="heading-md text-[#0A0A0A] mb-12 text-center">
              {language === 'it' ? 'Tracce GPS sentieri' : 'GPS Trail Tracks'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trails.map((trail, i) => (
                <div key={trail.id} className="bg-white p-6 rounded-lg border">
                  <h3 className="font-heading uppercase mb-2">{getTranslatedContent(trail.name)}</h3>
                  <p className="text-gray-600 text-sm mb-4">{getTranslatedContent(trail.description)}</p>
                  {trail.gpx_file_url && (
                    <a 
                      href={trail.gpx_file_url}
                      download
                      className="inline-flex items-center text-[#FF6D00] font-semibold text-sm hover:underline"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download GPX
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
